<template>
  <div class="ps-20 overflow-hidden">
    <div class="container">
      <div class="row section-header">
        <div class="col">
          <div class="title-blue">{{ t("SETTLE_PROGRAMME") }}</div>
        </div>
      </div>
      <div class="row padding-top96">
        <div class="col">
          <div class="second_title">{{ t("HOME_JUMBOTRON_HEADER") }}</div>
        </div>
      </div>
      <div class="row padding-top96">
        <div class="col">
          <text-and-picture :data="introduction" :picture-left="false"/>
        </div>
      </div>
      <div class="row section mx-auto align-items-center">
        <div class="col-xl-4 col-12 position-relative">
          <img id="rafael-image" :src="rafael.src" :style="rafael.style" alt=""/>
          <img class="background d-none d-xl-block" :style="rafael.mask.style" :src="rafael.mask.src" alt=""/>
        </div>
        <div class="col-xl-8 col-12">
          <div class="text18-black">{{ t("RAFAEL_SHARES") }}</div>
          <div class="title-blue">{{ t("RAFAEL_QUOTE") }}</div>
        </div>
      </div>
    </div>

    <div id="section-blue" class="section">
      <div class="container">
        <div class="row">
          <div class="col-sm-auto col-auto">
            <text-block content-id="whocanjoinBlock" v-on:save="save" :content="content.elements.whocanjoinBlock"/>
          </div>
          <div class="col-sm-auto col-auto">
            <text-block content-id="howToRegisterBlock" v-on:save="save" :content="content.elements.howToRegisterBlock"/>
          </div>
        </div>
      </div>
    </div>
    <div id="section-modules" class="section">
      <div class="container">
        <div class="row">
          <div class="col">
            <div class="text24-black mb-4 text-center text-md-left">{{ t("MODULES") }}</div>
          </div>
        </div>
        <div class="row">
          <div class="col-10 mx-auto col-md-6 mt-3 py-2" v-for="entry in showModules" :key="entry.title">
            <div class="row">
              <div class="col-4 col-lg-3">
                <img :src="entry.icon" :alt="entry.title"/>
              </div>
              <div class="col-8 col-lg-9 ps-sm-0 ps-md-3 ps-xl-0">
                <router-link class="text16-blue" :to="{ name: entry.module }">{{ t(entry.title) }}</router-link>
                <p class="text14-black">{{ entry.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="section-other" class="section">
      <div class="container">
        <div class="row">
          <div class="col-6">
            <div class="text24-white">{{ t("OTHER_SOURCES") }}</div>
            <div class="text18-white">
              {{ t("TRAINING_MATERIALS") }}
              <a class="text18-light-gray" href="https://issuu.com/settleinestonia/stacks" target="_blank" rel="noopener noreferrer">{{ t("HERE") }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div id="section-faq" class="section">
      <div class="container">
        <div class="row">
          <div class="col">
            <div class="text24-black">{{ t("FAQ") }}</div>
            <div v-for="(q, index) in showQuestions" :key="q.question" class="border-bottom no-gutters white-space">
              <div class="row py-3 programme-faq-row" :class="{ collapsed: openFaqIndex !== index }" @click="toggleFaq(index)">
                <div class="col-auto">
                  <div class="text16-blue bold">{{ q.question }}</div>
                </div>
                <div class="col-1 ms-auto text-end">
                  <i class="pi pi-chevron-down programme-faq-chevron closed"/>
                  <i class="pi pi-chevron-up programme-faq-chevron opened"/>
                </div>
              </div>
              <div class="row">
                <div class="col">
                  <div v-show="openFaqIndex === index" class="text16-black px-3 pb-3">{{ q.answer }}</div>
                </div>
              </div>
            </div>
            <Button class="mt-3" :label="showMore ? t('SHOW_LESS') : t('SHOW_MORE')" @click="showMore = !showMore"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

import {computed, onBeforeMount, onBeforeUnmount, onMounted, ref} from "vue";
import {injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";
import EventBus from "@/util/eventBus";
import {useRouter} from "vue-router";
import {usePageContent} from "@/mixins/pageContentMixin2";

import TextBlock from "@/views/components/TextBlock.vue";
import TextAndPicture from "@/views/components/TextAndPicture.vue";


import programme_classroom from "@/assets/programme_classroom.png";
import programme_classroom_background from "@/assets/programme_classroom_background.svg";
import icon_world from "@/assets/icon/icon_world.svg";
import icon_shield from "@/assets/icon/icon_shield.svg";
import icon_briefcase from "@/assets/icon/icon_briefcase.svg";
import icon_family from "@/assets/icon/icon_family.svg";
import icon_flag from "@/assets/icon/icon_flag.svg";
import icon_children from "@/assets/icon/icon_children.svg";
import programme_rafael from "@/assets/programme_rafael.png";
import programme_rafael_background from "@/assets/programme_rafael_background.svg";

const {getPageContent, savePageContent} = usePageContent();
const axios = injectAxios();
const {t, locale, tm} = useI18n();
const content = ref({elements: {}});
const pageName = "programme";
const router = useRouter();

const showMore = ref(false);
const section = ref("");
const openFaqIndex = ref(null);
function toggleFaq(index) {
  openFaqIndex.value = openFaqIndex.value === index ? null : index;
}
const rafael = ref({
  src: programme_rafael,
  style: "width: 360px",
  mask: {
    src: programme_rafael_background,
    style: "position: absolute; left: -167px; top: -145px;",
  },
});

onBeforeMount(() => {
  EventBus.$on("locale", loadPageContent);
  loadPageContent();
});
onMounted(() => {
  section.value = router.currentRoute.value.hash.replace("#", "");
  if (section.value) {
    globalThis.document.getElementById(section.value).scrollIntoView();
  }
});
onBeforeUnmount(() => {
  EventBus.$off("locale", loadPageContent);
});

async function loadPageContent() {
  content.value = await getPageContent(axios, pageName, locale.value);
}

const save = (elem) => {
  savePageContent(axios, pageName, locale.value, elem.element, elem.value);
};

const introduction = computed(() => {
  return {
    page: pageName,
    element: "introduction",
    image: {
      src: programme_classroom,
      style: "width: 360px",
      mask: {
        src: programme_classroom_background,
        style: "position: absolute; right: -417px; top: -141px",
      },
    },
    text1: content.value ? content.value.elements.introductionText1 : "",
    text2: content.value ? content.value.elements.introductionText2 : "",
  };
});

const showQuestions = computed(() => {
  return getQuestions();
});

const showModules = computed(() => {
  return getModuleDescription();
});

const getQuestions = () => {
  const questions = tm("FAQ_QUESTIONS");
  if (showMore.value) {
    return questions;
  }
  return questions.slice(0, 5);
};
const getModuleDescription = () => {
  return [
    {
      title: "BASIC_MODULE",
      icon: icon_world,
      desc: t("BASIC_MODULE_DESCRIPTION"),
      module: "BasicModule",
    },
    {
      title: "LANG_MODULE",
      icon: icon_flag,
      desc: t("LANG_MODULE_DESCRIPTION"),
      module: "LanguageModule",
    },
    {
      title: "PROTECTION_MODULE",
      icon: icon_shield,
      desc: t("PROTECTION_MODULE_DESCRIPTION"),
      module: "ProtectionModule",
    },
    {
      title: "FAMILY_MODULE",
      icon: icon_family,
      desc: t("FAMILY_MODULE_DESCRIPTION"),
      module: "FamilyModule",
    },
    {
      title: "TEMP_MODULE",
      icon: icon_family,
      desc: t("TEMP_MODULE_DESCRIPTION"),
      module: "TemporaryProtectionModule",
    },
    {
      title: "WORK_MODULE",
      icon: icon_briefcase,
      desc: t("WORK_MODULE_DESCRIPTION"),
      module: "WorkModule",
    },
    {
      title: "TEMP_LANG_MODULE",
      icon: icon_flag,
      desc: t("TEMP_LANG_MODULE_DESCRIPTION"),
      module: "TemporaryProtectionLangModule",
    },
    {
      title: "ENTREPRENEURSHIP_MODULE",
      icon: icon_children,
      desc: t("ENTREPRENEURSHIP_MODULE_DESCRIPTION"),
      module: "EntrepreneurshipModule",
    },
    
  ];
};
</script>

<style lang="scss" scoped>
#section-blue {
  background:
    linear-gradient(
      90deg,
      rgba(0, 0, 150, 0.98) 35.15%,
      rgba(0, 0, 240, 0.55) 99.98%
    ),
    url("@/assets/programme_cafe.jpg") no-repeat center;
  background-size: cover;
}

#section-modules {
  background-color: var(--p-color-white-50);
}

#section-other {
  background:
    linear-gradient(90deg, #3c0078 0%, rgba(60, 0, 120, 0.35) 100%),
    url("@/assets/programme_writing.jpg") no-repeat right top;
  background-size: cover;
  padding-left: 20px;
}

:deep(.table-btn-cell) {
  max-width: 100px;
  text-align: right;
}

.padding-top96 {
  padding-top: 96px;
  padding-left: 20px;
}

.white-space {
  white-space: pre-line;
}
.programme-faq-row {
  cursor: pointer;
}
.programme-faq-chevron {
  color: blue;
}
.programme-faq-row.collapsed .programme-faq-chevron.opened,
.programme-faq-row:not(.collapsed) .programme-faq-chevron.closed {
  display: none;
}
.programme-faq-row.collapsed .programme-faq-chevron.closed,
.programme-faq-row:not(.collapsed) .programme-faq-chevron.opened {
  display: inline;
}

.second_title {
  font-size: 25px;
  line-height: 2;
  color: var(--p-color-blue-600);
  text-transform: capitalize;
  font-family: "Aino Headline", sans-serif;
}
</style>
